import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { SerializedEditorState } from 'lexical';
import { FormProvider, useForm } from 'react-hook-form';
import { RichTextEditor } from 'components/Editor';
import { AgreementInputValues, AgreementOutputValues, AgreementSchema } from './AgreementSchema';
import AgreementContainer from 'components/Editor/ContentContainers/AgreementContainer';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * Props for the AgreementForm component.
 *
 * @interface EmailTemplateFormProps
 */
interface EmailTemplateFormProps {
  /**
   * Callback function called when the form is submitted successfully.
   * Receives the validated agreement version output values.
   * Should return a Promise that resolves when submission is complete.
   *
   * @param {AgreementVersionOutputValues} data - The validated form data.
   * @returns {Promise<void>}
   */
  onSubmit: (data: AgreementOutputValues) => Promise<void>;

  /**
   * The initial agreement data.
   * @type {AgreementInputValues}
   */
  initialData?: AgreementInputValues;

  /**
   * Whether the editor is loading and should display a skeleton.
   *
   * @type {boolean}
   */
  isLoading?: boolean;
}

export function AgreementForm({ initialData, isLoading, onSubmit }: EmailTemplateFormProps) {

  const form = useForm<AgreementInputValues, unknown, AgreementOutputValues>({
    mode: 'all',
    resolver: zodResolver(AgreementSchema),
  });

  useEffect(() => {
    const formErrors = form.formState.errors;
    console.log('Form Errors: ', formErrors);
    console.log('Form Value: ', form.getValues());
  }, [form, form.formState.errors]);

  useEffect(() => {
    console.log('Form Dirty: ', form.formState.isDirty);
    console.log('Form Dirty Fields: ', form.formState.dirtyFields);
  }, [form.formState.dirtyFields, form.formState.isDirty]);

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <Stack>
          <RichTextEditor
            contentEditableWrapper={AgreementContainer}
            toolbarButtons={
              <Stack direction="row" justifyContent="flex-end" flexGrow={1} spacing={1}>
                <Button variant="contained" type="submit">
                  Save
                </Button>
              </Stack>
            }
            initialState={initialData?.content as SerializedEditorState}
            onChange={(state) => form.setValue('content', state, { shouldDirty: true, shouldValidate: true })}
            isLoading={isLoading}
          />
        </Stack>
        <Backdrop
          open={form.formState.isSubmitting}
          sx={{
            zIndex: (theme) => (theme.zIndex.drawer + 1),
          }}
        >
          <CircularProgress />
        </Backdrop>
      </form>
    </FormProvider>
  );
}

export default AgreementForm;
